import toast from "react-hot-toast";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  addStockToWarehouseApi,
  downloadProductStockReportForAdminApi,
  fetchAllProducts,
  fetchProductsByWarehousPaginationeApi,
  fetchProductsListApi,
  getProductByIdApi,
  getProductsCostByWarehouse,
  getProductsStockForAdminViewPagination,
  handleAddProduct,
  handleDeleteProduct,
  handleUpdateProduct,
  resendWarrantyDetailsApi,
} from "../../api/product.api";
import { IAllRange, IRange, IProduct } from "../../models/productData.model";
import { fetchAllRanges, handleAddRange } from "../../api/range.api";

export interface ICostPair {
  totalPrice: number;
  totalPurchasedPrice: number;
}

interface ProductState {
  products: IProduct[];
  loading: boolean;
  warehouseLoading: boolean;
  downloadLoading: boolean;
  items: Record<string, IProduct>;
  ranges: IRange[];
  allRanges: IAllRange[];
  productNamesIds: { _id: string; name: string }[];
  resendError: string | null;
  warehouseProducts: IProduct[];
  totalWarehousePages: number;
  currentWarehousePage: number;
  warehouseLimit: number;
  totalWarehouseItems: number;
  costByWarehouse: {
    products: {
      productId: string;
      productName: string;
      warehouses: Record<string, ICostPair>;
      grandTotal: ICostPair;
    }[];
    warehouseWiseTotal: Record<string, ICostPair>;
    overallTotal: ICostPair;
  };
  costLoading: boolean;
}

const initialState: ProductState = {
  items: {},
  products: [],
  loading: false,
  warehouseLoading: false,
  downloadLoading: false,
  ranges: [],
  allRanges: [],
  productNamesIds: [],
  resendError: null,
  warehouseProducts: [],
  totalWarehousePages: 0,
  currentWarehousePage: 1,
  warehouseLimit: 10,
  totalWarehouseItems: 0,
  costByWarehouse: {
    products: [],
    warehouseWiseTotal: {},
    overallTotal: { totalPrice: 0, totalPurchasedPrice: 0 },
  },
  costLoading: false,
};

export const getAllRange = createAsyncThunk(
  "ranges/fetchranges",
  async (_, { rejectWithValue }) => {
    try {
      const ranges = await fetchAllRanges();
      return ranges;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch products");
    }
  },
);

export const addRange = createAsyncThunk(
  "ranges/addRange",
  async (range: string, { rejectWithValue }) => {
    try {
      return await handleAddRange(range);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to add range");
    }
  },
);

export const fetchProducts = createAsyncThunk(
  "products/fetchProducts",
  async (_, { rejectWithValue }) => {
    try {
      const products = await fetchAllProducts();
      return products;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch products");
    }
  },
);

export const addProduct = createAsyncThunk(
  "products/addProduct",
  async (newProduct: any, { rejectWithValue }) => {
    try {
      return await handleAddProduct(newProduct);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to add product");
    }
  },
);

export const deleteProduct = createAsyncThunk(
  "products/deleteProduct",
  async (productId: string, { rejectWithValue }) => {
    try {
      return await handleDeleteProduct(productId);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to delete product");
    }
  },
);

export const updateProduct = createAsyncThunk(
  "products/updateProduct",
  async (
    { id, payload }: { id: string; payload: any },
    { rejectWithValue },
  ) => {
    try {
      const response = await handleUpdateProduct(id, payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to update product");
    }
  },
);

export const fetchProductsList = createAsyncThunk(
  "products/fetchProductsList",
  async (_, { rejectWithValue }) => {
    try {
      const products = await fetchProductsListApi();
      return products;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch products list");
    }
  },
);

export const getProductById = createAsyncThunk(
  "products/getProductById",
  async (productIds: string[], { rejectWithValue }) => {
    try {
      const product = await getProductByIdApi(productIds);
      return product;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch product");
    }
  },
);

export const resendWarrantyDetails = createAsyncThunk(
  "warranty/resend",
  async (
    payload: { id: string; mobileNumber?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await resendWarrantyDetailsApi(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.message);
    }
  },
);

export const fetchProductsByWarehousePagination = createAsyncThunk(
  "product/fetchByWarehouse",
  async (
    {
      warehouseId,
      page,
      limit,
      search,
      sortBy,
      sortOrder,
    }: {
      warehouseId: string;
      page: number;
      limit: number;
      search: string;
      sortBy?: string;
      sortOrder?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await fetchProductsByWarehousPaginationeApi(
        warehouseId,
        page,
        limit,
        search,
        sortBy,
        sortOrder,
      );
    } catch (error) {
      return rejectWithValue("Failed to fetch");
    }
  },
);

export const ProductsStockForAdminViewPagination = createAsyncThunk(
  "product/ProductsStockForAdminViewPagination",
  async (
    { page, limit, search }: { page: number; limit: number; search: string },
    { rejectWithValue },
  ) => {
    try {
      return await getProductsStockForAdminViewPagination(page, limit, search);
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch stock");
    }
  },
);

export const downloadProductStockReportForAdmin = createAsyncThunk(
  "product/downloadStockReport",
  async (
    {
      format,
      search,
      date,
    }: { format: "csv" | "xlsx"; search: string; date: string },
    { rejectWithValue },
  ) => {
    try {
      const blob = await downloadProductStockReportForAdminApi({
        format,
        search,
        date,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `ProductStock_${date}.${format}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error: any) {
      return rejectWithValue(
        error.message || `Failed to download ${format.toUpperCase()} report.`,
      );
    }
  },
);

export const fetchProductsCostByWarehouse = createAsyncThunk(
  "product/fetchCostByWarehouse",
  async (_, { rejectWithValue }) => {
    try {
      return await getProductsCostByWarehouse();
    } catch (error) {
      return rejectWithValue(error ?? "Failed to fetch cost");
    }
  },
);

export const addStockToWarehouse = createAsyncThunk(
  "products/addStockToWarehouse",
  async (
    {
      productId,
      warehouseId,
      quantity,
      productPrice,
      gst,
      transportPrice,
      handlingCost,
    }: {
      productId: string;
      warehouseId: string;
      quantity: number;
      productPrice: number;
      gst: number;
      transportPrice: number;
      handlingCost: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const { updatedQuantity } = await addStockToWarehouseApi({
        productId,
        warehouseId,
        quantity,
        productPrice,
        gst,
        transportPrice,
        handlingCost,
      });
      return { productId, updatedQuantity };
    } catch (error: any) {
      const message =
        error.response?.data?.message || error.message || "Failed to add stock";
      return rejectWithValue(message);
    }
  },
);

// Create slice with a local reducer to update ranges instantly
const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    updateRangeInstant: (
      state,
      action: PayloadAction<{
        rangeId: string;
        quantity: number;
        startData?: string;
        endData?: string;
      }>,
    ) => {
      const { rangeId, quantity, startData, endData } = action.payload;
      const existingRange = state?.ranges?.find((r) => r.rangeId === rangeId);
      if (existingRange) {
        existingRange.totalQuantity += Number(quantity);
      } else if (startData && endData) {
        state?.ranges?.push({
          rangeId,
          startData,
          endData,
          totalQuantity: Number(quantity),
        });
      }
    },
    clearError: (state) => {},
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProducts.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchProducts.fulfilled, (state, action) => {
      state.loading = false;
      state.products = action?.payload?.products;
      state.ranges = action?.payload?.ranges;
    });
    builder.addCase(fetchProducts.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(addProduct.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      addProduct.fulfilled,
      (state, action: PayloadAction<IProduct | undefined>) => {
        state.loading = false;
        if (!action.payload) {
          toast.error("No product data received in addProduct.fulfilled.");
          return;
        }
        state.products = state.products || [];
        state.products.push(action.payload);
        if (action.payload.range && action.payload.quantity) {
          const { range, quantity } = action.payload;
          const { rangeId, startData, endData } = range as unknown as IRange;
          state.ranges = state.ranges || [];
          const existingRange = state.ranges.find((r) => r.rangeId === rangeId);
          if (existingRange) {
            existingRange.totalQuantity += Number(quantity);
          } else if (startData && endData) {
            state.ranges.push({
              rangeId,
              startData,
              endData,
              totalQuantity: Number(quantity),
            });
          }
        }
      },
    );
    builder.addCase(addProduct.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(deleteProduct.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      deleteProduct.fulfilled,
      (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.products = state.products.filter(
          (product) => product._id !== action.payload,
        );
      },
    );
    builder.addCase(deleteProduct.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateProduct.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      updateProduct.fulfilled,
      (state, action: PayloadAction<IProduct>) => {
        state.loading = false;
        const index = state.products.findIndex(
          (product) => product._id === action.payload._id,
        );
        if (index !== -1) {
          state.products[index] = action.payload;
        }
      },
    );
    builder.addCase(updateProduct.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getAllRange.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllRange.fulfilled, (state, action) => {
      state.loading = false;
      state.allRanges = action.payload;
    });
    builder.addCase(getAllRange.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(addRange.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addRange.fulfilled, (state, action) => {
      state.loading = false;
      state.ranges?.push(action.payload);
    });
    builder.addCase(addRange.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(fetchProductsList.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchProductsList.fulfilled, (state, action) => {
      state.loading = false;
      state.products = action.payload || [];
    });
    builder.addCase(fetchProductsList.rejected, (state) => {
      state.loading = false;
    });
    builder
      .addCase(getProductById.fulfilled, (state, action) => {
        if (action.payload && action.payload.length > 0) {
          const product = action.payload[0];
          state.items[product._id] = product;
        }
      })
      .addCase(getProductById.rejected, (state, action) => {
        toast.error(action.payload as string);
      })
      .addCase(getProductById.pending, (state) => {
        state.loading = true;
      })
      .addCase(resendWarrantyDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(resendWarrantyDetails.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resendWarrantyDetails.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchProductsByWarehousePagination.pending, (state) => {
        state.warehouseLoading = true;
      })
      .addCase(fetchProductsByWarehousePagination.rejected, (state) => {
        state.warehouseLoading = false;
      })
      .addCase(
        fetchProductsByWarehousePagination.fulfilled,
        (state, action) => {
          state.warehouseLoading = false;
          state.warehouseProducts = action.payload.products;
          state.totalWarehousePages = action.payload.totalPages;
          state.currentWarehousePage = action.payload.currentPage;
          state.warehouseLimit = action.payload.limit;
        },
      )
      .addCase(ProductsStockForAdminViewPagination.pending, (state) => {
        state.warehouseLoading = true;
      })
      .addCase(
        ProductsStockForAdminViewPagination.fulfilled,
        (state, action) => {
          state.warehouseLoading = false;
          state.warehouseProducts = action.payload.products;
          state.totalWarehousePages = action.payload.totalPages;
          state.currentWarehousePage = action.payload.currentPage;
          state.totalWarehouseItems = action.payload.totalItems;
          state.warehouseLimit = action.payload.limit;
        },
      )
      .addCase(ProductsStockForAdminViewPagination.rejected, (state) => {
        state.warehouseLoading = false;
      })
      .addCase(downloadProductStockReportForAdmin.pending, (state) => {
        state.downloadLoading = true;
      })
      .addCase(downloadProductStockReportForAdmin.fulfilled, (state) => {
        state.downloadLoading = false;
      })
      .addCase(downloadProductStockReportForAdmin.rejected, (state) => {
        state.downloadLoading = false;
      })
      .addCase(fetchProductsCostByWarehouse.pending, (state) => {
        state.costLoading = true;
      })
      .addCase(fetchProductsCostByWarehouse.rejected, (state) => {
        state.costLoading = false;
      })
      .addCase(fetchProductsCostByWarehouse.fulfilled, (state, action) => {
        state.costLoading = false;
        state.costByWarehouse = action.payload;
      })
      .addCase(addStockToWarehouse.pending, (state) => {
        state.loading = true;
      })
      .addCase(addStockToWarehouse.fulfilled, (state, action) => {
        state.loading = false;
        const { productId, updatedQuantity } = action.payload;

        const productIndex = state.products.findIndex(
          (p) => p._id === productId,
        );
        if (productIndex !== -1) {
          state.products[productIndex].quantity = updatedQuantity;
        }

        if (state.items[productId]) {
          state.items[productId].quantity = updatedQuantity;
        }
      })
      .addCase(addStockToWarehouse.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload as string);
      });
  },
});

export const { updateRangeInstant } = productSlice.actions;
export default productSlice.reducer;
